import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — Rahmen der Auswertungstabellen.
 *
 * Alle Tabellen der Seite sind gleich gebaut: waagerecht scrollbar (die Spaltenzahl passt auf
 * schmalen Fenstern nicht), Zahlen rechtsbündig mit gleicher Ziffernbreite, und eine leere
 * Ergebnismenge sagt das im Klartext. Eine leere Tabelle ohne Satz sähe aus wie ein Ladefehler.
 */
export interface Spalte {
  titel: string;
  /** true = rechtsbündig mit Tabellenziffern (alle Zähler und Anteile). */
  numerisch?: boolean;
}

/**
 * Zusätzliche Kopfzeile über zusammengehörigen Spalten (RITA-1515: je Kennzahl „Median“ und
 * „95 %“ unter einem gemeinsamen Titel mit Einheit). `spalten` ist die Zahl der überspannten
 * Spalten; ein leerer Titel lässt die Zelle leer (führende Spalten ohne Gruppe).
 */
export interface KopfGruppe {
  titel: string;
  spalten: number;
}

export interface DatenTabelleProps {
  spalten: Spalte[];
  /** Optionale Gruppenzeile über den Spaltentiteln; die Summe der Breiten muss `spalten` decken. */
  kopfgruppen?: KopfGruppe[];
  /** true = keine Zeilen; statt der leeren Tabelle erscheint `leerText`. */
  leer: boolean;
  leerText: string;
  children: React.ReactNode;
}

export const zahlenZelle = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };

export default function DatenTabelle({ spalten, kopfgruppen, leer, leerText, children }: DatenTabelleProps) {
  if (leer) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {leerText}
        </Typography>
      </Paper>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          {kopfgruppen && (
            <TableRow>
              {kopfgruppen.map((gruppe, stelle) => (
                // Gruppen sind fest angeordnet; die Stelle ist der stabile Schlüssel (Titel können leer sein).
                // eslint-disable-next-line react/no-array-index-key
                <TableCell key={stelle} colSpan={gruppe.spalten} align="center" sx={{ borderBottom: 0, pb: 0 }}>
                  {gruppe.titel}
                </TableCell>
              ))}
            </TableRow>
          )}
          <TableRow>
            {spalten.map(({ titel, numerisch }, stelle) => (
              // Titel dürfen sich unter verschiedenen Gruppen wiederholen („Median“, „95 %“).
              // eslint-disable-next-line react/no-array-index-key
              <TableCell key={`${stelle}-${titel}`} sx={numerisch ? zahlenZelle : undefined}>
                {titel}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );
}
